import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import api from "../lib/api";
import { uploadToCloudinary, MODERATION_ERROR } from "../lib/cloudinary";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import PollCard from "../components/PollCard";
import { MoreHorizontal, Camera, Loader2 } from "lucide-react";

const ProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [polls, setPolls] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followingOpen, setFollowingOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: "", bio: "", avatar_url: "" });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const avatarInputRef = useRef(null);

  const profileUsername = useMemo(() => username || user?.username, [username, user]);

  const loadProfile = useCallback(async () => {
    if (!profileUsername) {
      navigate("/auth");
      return;
    }
    try {
      const response = await api.get(`/users/${profileUsername}`);
      setProfile(response.data);
      setEditForm({
        full_name: response.data.full_name || "",
        bio: response.data.bio || "",
        avatar_url: response.data.avatar_url || "",
      });
    } catch (error) {
      toast.error("Profile not found.");
    }
  }, [profileUsername, navigate]);

  const loadPolls = useCallback(async () => {
    if (!profileUsername) return;
    try {
      const response = await api.get(`/users/${profileUsername}/polls`);
      setPolls(response.data);
    } catch (error) {
      toast.error("Unable to load profile polls.");
    }
  }, [profileUsername]);

  useEffect(() => {
    loadProfile();
    loadPolls();
  }, [loadProfile, loadPolls]);

  const handleFollow = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    try {
      if (profile.is_following) {
        await api.delete(`/users/${profile.id}/follow`);
        setProfile((prev) => ({ ...prev, is_following: false, followers_count: prev.followers_count - 1 }));
      } else {
        await api.post(`/users/${profile.id}/follow`);
        setProfile((prev) => ({ ...prev, is_following: true, followers_count: prev.followers_count + 1 }));
      }
    } catch (error) {
      toast.error("Unable to update follow status.");
    }
  };

  const openFollowers = async () => {
    setFollowersOpen(true);
    try {
      const response = await api.get(`/users/${profile.id}/followers`);
      setFollowers(response.data);
    } catch (error) {
      toast.error("Unable to load followers.");
    }
  };

  const openFollowing = async () => {
    setFollowingOpen(true);
    try {
      const response = await api.get(`/users/${profile.id}/following`);
      setFollowing(response.data);
    } catch (error) {
      toast.error("Unable to load following.");
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    setAvatarUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      setEditForm((prev) => ({ ...prev, avatar_url: url }));
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err?.message === MODERATION_ERROR ? MODERATION_ERROR : "Photo upload failed.");
      setAvatarPreview(null);
    } finally {
      setAvatarUploading(false);
    }
  };

  const saveProfile = async () => {
    try {
      await api.put("/users/me", editForm);
      toast.success("Profile updated.");
      setEditOpen(false);
      refreshUser();
      loadProfile();
    } catch (error) {
      toast.error("Unable to update profile.");
    }
  };

  const updatePoll = (updatedPoll) => {
    setPolls((prev) => prev.map((poll) => (poll.id === updatedPoll.id ? updatedPoll : poll)));
  };

  const removePoll = (pollId) => {
    setPolls((prev) => prev.filter((poll) => poll.id !== pollId));
  };

  if (!profile) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="profile-loading">
        Loading profile...
      </div>
    );
  }

  const isOwner = user && profile.username === user.username;

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="profile-header">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="flex flex-wrap items-center gap-5">
            <img
              src={profile.avatar_url}
              alt={profile.username}
              className="h-28 w-28 rounded-full object-cover ring-4 ring-background shadow-lg"
              data-testid="profile-avatar"
            />
            <div className="space-y-1">
              <div className="text-xl font-semibold" data-testid="profile-name">
                {profile.full_name}
              </div>
              <div className="text-sm text-muted-foreground" data-testid="profile-username">
                @{profile.username}
              </div>
              {profile.verified && (
                <div className="text-xs text-emerald-600" data-testid="profile-verified">
                  Verified
                </div>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-10 w-10 rounded-full border border-border flex items-center justify-center"
                data-testid="profile-menu-trigger"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="profile-menu-content">
              <DropdownMenuItem onClick={() => navigate("/manage-notifications")} data-testid="profile-menu-notifications">
                Notifications
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/saved-polls")} data-testid="profile-menu-saved-polls">
                Saved polls
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="profile-menu-admin">
                  Admin View
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/account-privacy")} data-testid="profile-menu-account-privacy">
                Account privacy
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/your-activity")} data-testid="profile-menu-activity">
                Your activity
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/privacy-policy")} data-testid="profile-menu-privacy-policy">
                Privacy Policy
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/terms")} data-testid="profile-menu-terms">
                Terms &amp; Conditions
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/community-guidelines")} data-testid="profile-menu-guidelines">
                Community Guidelines
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <p className="text-sm text-muted-foreground" data-testid="profile-bio">
          {profile.bio || "No bio yet."}
        </p>
        <div className="flex flex-wrap gap-6 text-sm" data-testid="profile-stats">
          <div className="text-left" data-testid="profile-polls-count">
            <div className="font-semibold" data-testid="profile-polls-value">
              {polls.length}
            </div>
            <div className="text-muted-foreground" data-testid="profile-polls-label">Polls</div>
          </div>
          <button onClick={openFollowers} className="text-left" data-testid="profile-followers-button">
            <div className="font-semibold" data-testid="profile-followers-count">
              {profile.followers_count}
            </div>
            <div className="text-muted-foreground">Followers</div>
          </button>
          <button onClick={openFollowing} className="text-left" data-testid="profile-following-button">
            <div className="font-semibold" data-testid="profile-following-count">
              {profile.following_count}
            </div>
            <div className="text-muted-foreground">Following</div>
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {isOwner ? (
            <>
              <Button onClick={() => { setEditOpen(true); setAvatarPreview(null); }} data-testid="profile-edit-button">
                Edit profile
              </Button>
              <Button variant="secondary" onClick={logout} data-testid="profile-logout-button">
                Log out
              </Button>
            </>
          ) : (
            <Button onClick={handleFollow} data-testid="profile-follow-button">
              {profile.is_following ? "Unfollow" : "Follow"}
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4" data-testid="profile-polls">
        <h3 className="text-lg font-semibold" data-testid="profile-polls-title">
          Polls by {profile.username}
        </h3>
        {polls.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="profile-polls-empty">
            No polls yet.
          </div>
        ) : (
          polls.map((poll) => (
            <PollCard key={poll.id} poll={poll} onUpdate={updatePoll} onRemove={removePoll} />
          ))
        )}
      </div>

      <Dialog open={followersOpen} onOpenChange={setFollowersOpen}>
        <DialogContent data-testid="followers-dialog">
          <DialogHeader>
            <DialogTitle data-testid="followers-title">Followers</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="followers-list">
            {followers.length === 0 ? (
              <div className="text-sm text-muted-foreground" data-testid="followers-empty">
                No followers yet.
              </div>
            ) : (
              followers.map((follower) => (
                <div key={follower.id} className="flex items-center gap-3" data-testid={`follower-${follower.id}`}>
                  <img
                    src={follower.avatar_url}
                    alt={follower.username}
                    className="h-8 w-8 rounded-full object-cover"
                    data-testid={`follower-avatar-${follower.id}`}
                  />
                  <div>
                    <div className="text-sm font-semibold" data-testid={`follower-username-${follower.id}`}>
                      @{follower.username}
                    </div>
                    <div className="text-xs text-muted-foreground" data-testid={`follower-name-${follower.id}`}>
                      {follower.full_name}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={followingOpen} onOpenChange={setFollowingOpen}>
        <DialogContent data-testid="following-dialog">
          <DialogHeader>
            <DialogTitle data-testid="following-title">Following</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="following-list">
            {following.length === 0 ? (
              <div className="text-sm text-muted-foreground" data-testid="following-empty">
                Not following anyone yet.
              </div>
            ) : (
              following.map((followed) => (
                <div key={followed.id} className="flex items-center gap-3" data-testid={`following-${followed.id}`}>
                  <img
                    src={followed.avatar_url}
                    alt={followed.username}
                    className="h-8 w-8 rounded-full object-cover"
                    data-testid={`following-avatar-${followed.id}`}
                  />
                  <div>
                    <div className="text-sm font-semibold" data-testid={`following-username-${followed.id}`}>
                      @{followed.username}
                    </div>
                    <div className="text-xs text-muted-foreground" data-testid={`following-name-${followed.id}`}>
                      {followed.full_name}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="profile-edit-dialog">
          <DialogHeader>
            <DialogTitle data-testid="profile-edit-title">Edit profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <img
                  src={avatarPreview || editForm.avatar_url || profile.avatar_url}
                  alt="Avatar"
                  className="h-24 w-24 rounded-full object-cover ring-2 ring-border"
                  data-testid="profile-edit-avatar-preview"
                />
                <button
                  type="button"
                  className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                  data-testid="profile-edit-avatar-upload-button"
                >
                  {avatarUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  data-testid="profile-edit-avatar-file-input"
                />
              </div>
              {avatarUploading && (
                <span className="text-xs text-muted-foreground" data-testid="profile-edit-avatar-uploading">
                  Uploading photo...
                </span>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" data-testid="profile-edit-name-label">
                Full name
              </label>
              <Input
                value={editForm.full_name}
                onChange={(event) => setEditForm({ ...editForm, full_name: event.target.value })}
                data-testid="profile-edit-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" data-testid="profile-edit-bio-label">
                Bio
              </label>
              <Textarea
                rows={3}
                value={editForm.bio}
                onChange={(event) => setEditForm({ ...editForm, bio: event.target.value })}
                data-testid="profile-edit-bio-input"
              />
            </div>
            <Button onClick={saveProfile} disabled={avatarUploading} data-testid="profile-edit-save-button">
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
